import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarDays, Truck, Wrench, LogOut, Shield, Activity, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@features/auth/context/AuthContext';
import type { Technician, Vehicle } from '@shared/types';
import type { Tool } from '@shared/types/inventory';
import { getApiUrl } from '@/config';
import { isWithinInterval, parseISO, startOfDay, endOfDay } from 'date-fns';

interface DashboardPageProps {
    technicians?: Technician[];
    tools?: Tool[];
}

export const DashboardPage: React.FC<DashboardPageProps> = ({ technicians = [], tools = [] }) => {
    const navigate = useNavigate();
    const { logout, user } = useAuth();
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);

    useEffect(() => {
        // Fetch vehicles for dashboard stats
        const token = localStorage.getItem('token');
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        fetch(getApiUrl('/api/vehicles'), { headers })
            .then(res => res.ok ? res.json() : [])
            .then(data => setVehicles(data))
            .catch(err => console.error("Failed to load vehicles for dashboard", err));
    }, []);

    // --- Compute Stats ---
    const today = new Date();

    // 1. Assignments Today
    const assignmentsTodayCount = technicians.reduce((acc, tech) => {
        const activeToday = tech.assignments.filter(a => {
            try {
                const start = parseISO(a.startDate);
                const end = parseISO(a.endDate);
                return isWithinInterval(today, { start: startOfDay(start), end: endOfDay(end) });
            } catch { return false; }
        }).length;
        return acc + activeToday;
    }, 0);

    // 2. Fleet Availability
    const availableVehiclesCount = vehicles.filter(v => v.status === 'Disponible').length;
    const totalVehicles = vehicles.length;
    const fleetHealth = totalVehicles > 0 ? Math.round((availableVehiclesCount / totalVehicles) * 100) : 0;

    // 3. Tools Issues (Maintenance or Lost)
    const toolsIssueCount = tools.filter(t => t.status === 'maintenance' || t.status === 'lost').length;

    const hasAccess = (module: 'planning' | 'fleet' | 'tooling') => {
        if (user?.role === 'admin') return true;
        const perms = user?.permissions || {
            planning: user?.role === 'planner' ? 'write' : 'none',
            fleet: user?.role === 'planner' ? 'write' : 'none',
            tooling: user?.role === 'planner' ? 'write' : 'none'
        };
        return perms[module] !== 'none';
    };

    const modules = [];

    if (hasAccess('planning')) {
        modules.push({
            title: 'Planning & RH',
            description: 'Gérer les techniciens, les congés et les interventions.',
            icon: <CalendarDays size={48} />,
            color: 'bg-blue-500',
            path: '/planning'
        });
    }

    if (hasAccess('fleet')) {
        modules.push({
            title: 'Flotte Véhicules',
            description: 'Suivi du parc automobile, contrôles et attributions.',
            icon: <Truck size={48} />,
            color: 'bg-orange-500',
            path: '/fleet'
        });
    }

    if (hasAccess('tooling')) {
        modules.push({
            title: 'Outillage & Stock',
            description: 'Inventaire matériel, affectations et consommables.',
            icon: <Wrench size={48} />,
            color: 'bg-emerald-500',
            path: '/inventory'
        });
    }

    if (user?.role === 'admin') {
        modules.push({
            title: 'Administration',
            description: 'Sauvegardes, logs et configuration du système.',
            icon: <Shield size={48} />,
            color: 'bg-red-500',
            path: '/settings'
        });
    }

    return (
        <div className="min-h-screen p-8 max-w-7xl mx-auto flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-12">
                <div>
                    <h1 className="text-3xl font-bold text-[var(--text-main)] mb-2">Tableau de Bord</h1>
                    <p className="text-[var(--text-muted)]">Bienvenue, {user?.username}</p>
                </div>
                {/* Only show logout button if NOT in sidebar mode (as sidebar has it) */}
                <button
                    onClick={logout}
                    className="glass-button text-[var(--text-muted)] hover:text-red-400 flex items-center gap-2 px-4 py-2 md:hidden"
                >
                    <LogOut size={20} />
                    <span>Déconnexion</span>
                </button>
            </div>

            {/* Quick Stats Banner */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
            >
                {/* Stat 1: Activity */}
                {hasAccess('planning') && (
                    <div className="glass-panel p-6 flex items-center gap-5 border border-blue-500/20 bg-blue-500/5 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-8 bg-blue-500/10 rounded-full -mr-4 -mt-4 blur-xl"></div>
                        <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400">
                            <Activity size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-blue-300/80 font-medium">Activité Aujourd'hui</p>
                            <p className="text-2xl font-bold text-white mt-1">{assignmentsTodayCount} <span className="text-sm font-normal text-gray-400">interventions</span></p>
                        </div>
                    </div>
                )}
                
                {/* Stat 2: Fleet */}
                {hasAccess('fleet') && (
                    <div className="glass-panel p-6 flex items-center gap-5 border border-orange-500/20 bg-orange-500/5 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-8 bg-orange-500/10 rounded-full -mr-4 -mt-4 blur-xl"></div>
                        <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400">
                            <Truck size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-orange-300/80 font-medium">Disponibilité Flotte</p>
                            <div className="flex items-baseline gap-2 mt-1">
                                <p className="text-2xl font-bold text-white">{availableVehiclesCount} <span className="text-sm font-normal text-gray-400">/ {totalVehicles}</span></p>
                                {fleetHealth < 50 && <span className="text-xs text-red-400 flex items-center gap-1"><AlertCircle size={10} /> Tension</span>}
                            </div>
                        </div>
                    </div>
                )}

                {/* Stat 3: Alerts */}
                {hasAccess('tooling') && (
                    <div className="glass-panel p-6 flex items-center gap-5 border border-emerald-500/20 bg-emerald-500/5 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-8 bg-emerald-500/10 rounded-full -mr-4 -mt-4 blur-xl"></div>
                        <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                            {toolsIssueCount > 0 ? <AlertCircle size={24} className="text-red-400" /> : <CheckCircle2 size={24} />}
                        </div>
                        <div>
                            <p className="text-sm text-emerald-300/80 font-medium">Santé Outillage</p>
                            <p className="text-2xl font-bold text-white mt-1">
                                {toolsIssueCount} <span className="text-sm font-normal text-gray-400">alerte{toolsIssueCount > 1 ? 's' : ''}</span>
                            </p>
                        </div>
                    </div>
                )}
            </motion.div>

            {/* Module Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {modules.map((module, idx) => (
                    <motion.div
                        key={module.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 + 0.2 }}
                        onClick={() => navigate(module.path)}
                        className="glass-panel p-8 cursor-pointer group hover:bg-[var(--glass-border)] transition-all hover:scale-[1.02] relative overflow-hidden text-[var(--text-main)]"
                    >
                        <div className={`absolute top-0 right-0 p-32 opacity-5 rounded-full blur-3xl -mr-16 -mt-16 ${module.color}`} />

                        <div className={`w-16 h-16 rounded-2xl ${module.color} flex items-center justify-center text-white shadow-lg mb-6 group-hover:shadow-2xl transition-shadow`}>
                            {module.icon}
                        </div>

                        <h2 className="text-xl font-bold mb-3">{module.title}</h2>
                        <p className="text-sm text-[var(--text-muted)] leading-relaxed">
                            {module.description}
                        </p>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};
